<template>
  <div class="bump-chart-container">
    <!-- Controls always above chart -->
    <div class="chart-controls-bar">
      <div class="controls-group">
        <div class="min-docs-filter">
          <span class="filter-label">Min Docs:</span>
          <input
            v-model.number="minDocumentCount"
            type="number"
            :min="1"
            :max="documentsLength"
            class="compact-number-input"
          />
        </div>
        
        <!-- Proficiency Legend - only show when matrix is active -->
        <div v-if="displayMode === 'matrix'" class="proficiency-legend-inline">
          <span class="legend-label-inline">Proficiency:</span>
          <div class="legend-items-inline">
            <div 
              v-for="(level, index) in proficiencyLevels" 
              :key="'legend-inline-' + index"
              class="legend-item-inline"
            >
              <div 
                class="legend-square-inline" 
                :style="{ backgroundColor: getProficiencyColor(level) }"
              >
                {{ getProficiencyAbbreviation(level) }}
              </div>
              <span class="legend-text-inline">{{ level }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="chart-content" v-if="documentsLength > 0">
      <!-- Main Chart Area -->
      <div class="chart-wrapper">
        <div class="chart-scroll-container">
          <!-- Bump Chart SVG -->
          <svg v-if="displayMode === 'bump'" class="chart" ref="chart" :style="{ width: chartDisplayWidth + 'px' }">
            <!-- Grid lines -->
            <g class="grid-lines">
              <g v-for="(y, yIndex) in yGridLines" :key="'ygrid-' + yIndex">
                <line :x1="leftMargin" :x2="chartDisplayWidth - rightMargin" :y1="y" :y2="y" stroke="#f0f0f0" stroke-width="1"/>
              </g>
              <g v-for="(x, xIndex) in xGridLines" :key="'xgrid-' + xIndex">
                <line :x1="x" :x2="x" :y1="topMargin" :y2="chartHeight - bottomMargin" stroke="#f0f0f0" stroke-width="1"/>
              </g>
            </g>

            <!-- Axes -->
            <g class="axes">
              <line :x1="leftMargin" :x2="leftMargin" :y1="topMargin" :y2="chartHeight - bottomMargin" stroke="#333" stroke-width="2"/>
              <line :x1="leftMargin" :x2="chartDisplayWidth - rightMargin" :y1="chartHeight - bottomMargin" :y2="chartHeight - bottomMargin" stroke="#333" stroke-width="2"/>
            </g>

            <!-- Y-axis labels (Sequence numbers) -->
            <g class="y-axis-labels">
              <text :x="leftMargin - 10" y="30" text-anchor="middle" font-size="12" fill="#666" font-weight="bold">Priority</text>
              <g v-for="(seq, seqIndex) in allSequences" :key="'ylabel-' + seqIndex">
                <!-- Highlight background for target sequence during drag -->
                <rect v-if="dragState.isDragging && currentTargetSequence === seq"
                      :x="leftMargin - 25"
                      :y="yPositionCache[seq] - 10"
                      width="20"
                      height="20"
                      fill="#3b82f6"
                      opacity="0.3"
                      rx="3"
                      class="priority-highlight"/>
                      
                <text :x="leftMargin - 10" 
                      :y="yPositionCache[seq]" 
                      text-anchor="end" 
                      dominant-baseline="middle" 
                      font-size="12" 
                      :fill="dragState.isDragging && currentTargetSequence === seq ? '#3b82f6' : '#666'"
                      :font-weight="dragState.isDragging && currentTargetSequence === seq ? 'bold' : 'normal'"
                      class="y-axis-label">
                  {{ seq }}
                </text>
                
                <!-- Snap indicator line -->
                <line v-if="dragState.isDragging && currentTargetSequence === seq"
                      :x1="leftMargin"
                      :x2="chartDisplayWidth - rightMargin"
                      :y1="yPositionCache[seq]"
                      :y2="yPositionCache[seq]"
                      stroke="#3b82f6"
                      stroke-width="2"
                      opacity="0.6"
                      stroke-dasharray="5,5"
                      class="snap-line"/>
              </g>
            </g>

            <!-- X-axis labels (Documents) -->
            <g class="x-axis-labels">
              <g v-for="(doc, i) in processedDocuments" :key="'xlabel-' + i">
                <!-- Add skill icon for missing focused skills - positioned above document names -->
                <g v-if="getDocumentMissingFocusedSkills(doc).length > 0">
                  <circle
                    :cx="xPositionCache[i]"
                    :cy="chartHeight - bottomMargin + 5"
                    r="12"
                    fill="#10b981"
                    stroke="white"
                    stroke-width="2"
                    class="add-skill-icon"
                    @click="showAddSkillModal(doc, i)"
                    style="cursor: pointer;"
                  />
                  <text
                    :x="xPositionCache[i]"
                    :y="chartHeight - bottomMargin + 5"
                    text-anchor="middle"
                    dominant-baseline="middle"
                    font-size="14"
                    fill="white"
                    font-weight="bold"
                    style="pointer-events: none;"
                  >+</text>
                </g>
                
                <g v-for="(line, lineIndex) in doc.wrappedName" :key="'line-' + lineIndex">
                  <text :x="xPositionCache[i]" 
                        :y="chartHeight - bottomMargin + 35 + (lineIndex * 14)" 
                        text-anchor="middle" 
                        font-size="11" 
                        fill="#666" 
                        font-weight="bold">
                    {{ line }}
                  </text>
                </g>
              </g>
            </g>

            <!-- Connecting lines between documents for same skills -->
            <g class="connecting-lines">
              <g v-for="skill in visibleSkills" :key="'lines-' + skill.name">
                <!-- Only draw segments for non-deleted skills (sequence !== null) -->
                <g v-for="(segment, segmentIndex) in skill.segments" :key="'segment-' + segmentIndex">
                  <path :d="segment.pathData" 
                        :stroke="skill.color" 
                        stroke-width="3" 
                        fill="none" 
                        :opacity="segment.opacity"
                        style="cursor: pointer"
                        @click="!dragState.isDragging && !justFinishedDrag && handleSkillClick(skill.name)"/>
                </g>
              </g>
            </g>

            <!-- Data points for each document -->
            <g class="data-points">
              <g v-for="point in visibleDataPoints" :key="point.key">
                <!-- Skill point -->
                <circle
                  v-if="!point.isDeleted"
                  :cx="point.x"
                  :cy="dragState.isDragging && dragState.draggedSkill === point.skill ? yPositionCache[currentTargetSequence] : point.y"
                  r="6"
                  :fill="point.color"
                  stroke="white"
                  stroke-width="2"
                  :opacity="point.opacity"
                  @mouseenter="!dragState.isDragging && handleMouseEnter($event, point)"
                  @mouseleave="!dragState.isDragging && handleMouseLeave()"
                  @click="!dragState.isDragging && !justFinishedDrag && handleSkillPointClick(point)"
                  @mousedown="!point.isDeleted && handleMouseDown($event, point)"
                  :style="{ 
                    cursor: point.isDeleted ? 'not-allowed' : (localFocusedSkills.includes(point.skillName) ? 'grab' : 'pointer')
                  }"
                  :class="{ 
                    'skill-deleted-point': point.isDeleted,
                    'dragging': dragState.isDragging && dragState.draggedSkill === point.skill,
                    'draggable-skill': localFocusedSkills.includes(point.skillName) && !point.isDeleted,
                    'snapping': dragState.isDragging && dragState.draggedSkill === point.skill && currentTargetSequence !== dragState.originalSequence
                  }"/>

                <!-- Deleted skill indicator: show a different style for deleted/null-priority skills -->
                <g v-else>
                  <rect
                    :x="point.x - 8"
                    :y="point.y - 8"
                    width="16"
                    height="16"
                    rx="4"
                    fill="#fee2e2"
                    stroke="#dc2626"
                    stroke-width="2"
                    :opacity="point.opacity"
                    class="deleted-skill-rect"
                  />
                  <text
                    :x="point.x"
                    :y="point.y + 1"
                    text-anchor="middle"
                    dominant-baseline="middle"
                    font-size="13"
                    fill="#dc2626"
                    font-weight="bold"
                    class="deleted-skill-x"
                    :opacity="point.opacity"
                  >✕</text>
                  <!-- Undo button for deleted skill in bump chart -->
                  <foreignObject
                    :x="point.x + 8"
                    :y="point.y - 16"
                    width="24"
                    height="24"
                    v-if="point.isDeleted"
                    style="overflow: visible;"
                  >
                    <div style="display: flex; align-items: center;">
                      <v-btn
                        small
                        icon
                        color="success"
                        style="min-width: 0; padding: 0;"
                        @click.stop="undoDeleteSkillFromMatrix(point.document, point.skillName)"
                        title="Undo delete"
                      >
                        <v-icon size="14">mdi-undo</v-icon>
                      </v-btn>
                    </div>
                  </foreignObject>
                </g>
                
                <!-- Proficiency level display when skill is focused and not dulled and not deleted -->
                <text v-if="point.showProficiency && !point.isDulled && !point.isDeleted"
                      :x="point.x"
                      :y="point.y + 25"
                      text-anchor="middle"
                      dominant-baseline="middle"
                      font-size="12"
                      :fill="point.color"
                      font-weight="bold"
                      class="proficiency-label"
                      @click="!dragState.isDragging && !justFinishedDrag && showProficiencyModal(point)"
                      style="cursor: pointer;">
                  {{ point.rating || 'Unrated' }}
                </text>

                <!-- Priority change indicator - simplified for performance -->
                <g v-if="dragState.isDragging && dragState.draggedSkill === point.skill && currentTargetSequence !== dragState.originalSequence">
                  <!-- Ghost of original position -->
                  <circle :cx="point.x"
                          :cy="yPositionCache[dragState.originalSequence]"
                          r="3"
                          :fill="point.color"
                          opacity="0.4"
                          stroke="white"
                          stroke-width="1"/>
                  
                  <!-- Priority change text - simplified -->
                  <text :x="point.x + 20"
                        :y="yPositionCache[currentTargetSequence] - 10"
                        text-anchor="start"
                        dominant-baseline="middle"
                        font-size="11"
                        fill="#3b82f6"
                        font-weight="bold"
                        class="priority-change-text">
                    {{ currentTargetSequence }}
                  </text>
                </g>
              </g>
            </g>
          </svg>
          
          <!-- Heat Matrix Display -->
          <div v-else-if="displayMode === 'matrix'" class="heat-matrix-container">
            <div class="matrix-wrapper">
              <table class="matrix-table">
                <!-- Header row -->
                <thead>
                  <tr>
                    <th class="matrix-corner-header"></th>
                    <th 
                      v-for="(doc, index) in processedDocuments" 
                      :key="'header-' + index"
                      class="matrix-doc-header"
                    >
                      <div class="doc-header-content">
                        <span 
                          v-for="(line, lineIndex) in doc.wrappedName" 
                          :key="'header-line-' + lineIndex"
                          class="doc-header-line"
                        >
                          {{ line }}
                        </span>
                      </div>
                    </th>
                  </tr>
                </thead>
                
                <!-- Skill rows -->
                <tbody>
                  <tr v-for="skill in matrixSkills" :key="'skill-row-' + skill.name"
                    :class="{
                      'matrix-skill-focused': isSkillFocused(skill.name),
                      'matrix-skill-dulled': shouldDullSkill(skill.name)
                    }"
                  >
                    <!-- Skill name cell -->
                    <th class="matrix-skill-header">
                      <span class="skill-name">{{ skill.name }}</span>
                      <div class="skill-color-indicator" :style="{ backgroundColor: skill.color }"></div>
                    </th>
                    <!-- Proficiency cells for each document -->
                    <td 
                      v-for="(doc, docIndex) in processedDocuments" 
                      :key="'cell-' + skill.name + '-' + docIndex"
                      class="matrix-cell"
                      :class="[
                        {
                          'matrix-cell-clickable': getDocumentSkillForMatrix(doc, skill.name) && !getDocumentSkillForMatrix(doc, skill.name).isDeleted
                        },
                        isSkillFocused(skill.name)
                          ? 'matrix-cell-focused'
                          : (shouldDullSkill(skill.name) ? 'matrix-cell-dulled' : '')
                      ]"
                      @click="() => {
                        const s = getDocumentSkillForMatrix(doc, skill.name);
                        if (s && !s.isDeleted) handleMatrixCellClick(doc, skill.name)
                      }"
                    >
                      <div 
                        v-if="getDocumentSkillForMatrix(doc, skill.name) && !getDocumentSkillForMatrix(doc, skill.name).isDeleted"
                        class="proficiency-square"
                        :style="{ backgroundColor: getProficiencyColor(getDocumentSkillForMatrix(doc, skill.name).rating || 'Unrated') }"
                        :title="`${skill.name} - ${doc.displayName}: ${getDocumentSkillForMatrix(doc, skill.name).rating || 'Unrated'}`"
                      >
                        {{ getProficiencyAbbreviation(getDocumentSkillForMatrix(doc, skill.name).rating || 'Unrated') }}
                      </div>
                      <div 
                        v-else-if="getDocumentSkillForMatrix(doc, skill.name) && getDocumentSkillForMatrix(doc, skill.name).isDeleted"
                        class="proficiency-square deleted-matrix-skill"
                        title="Skill marked for deletion"
                        style="background: #fee2e2; color: #dc2626; border: 1px solid #fca5a5; position: relative; pointer-events: auto;"
                      >
                        <v-icon size="16" color="error" style="pointer-events: none;">mdi-delete</v-icon>
                        <!-- Undo button for deleted skill in matrix -->
                        <span
                          style="position: absolute; top: 2px; right: 2px; z-index: 2; pointer-events: auto;"
                        >
                          <v-btn
                            small
                            icon
                            color="success"
                            style="min-width: 0; padding: 0;"
                            @click.stop="undoDeleteSkillFromMatrix(doc, skill.name)"
                            title="Undo delete"
                          >
                            <v-icon size="14">mdi-undo</v-icon>
                          </v-btn>
                        </span>
                      </div>
                      <div v-else class="proficiency-square empty-cell" title="Skill not present">
                        <span class="add-skill-indicator" @click.stop="addSingleSkillToDocument(doc, skill.name)">+</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="no-data">
      <p>No document-level data available. Please select documents to view the bump chart.</p>
    </div>

    <div v-if="tooltip.show" class="tooltip" :style="{left: tooltip.x + 'px', top: tooltip.y + 'px'}">
      <div><strong>{{ tooltip.skill }}</strong></div>
      <div>{{ tooltip.document }}</div>
      <div>Priority: {{ tooltip.sequence }}</div>
      <div v-if="tooltip.proficiencyLevel">Proficiency: {{ tooltip.proficiencyLevel }}</div>
      <div v-if="tooltip.value">Value: {{ tooltip.value }}</div>
    </div>

    <!-- Proficiency Change Modal -->
    <v-dialog v-model="proficiencyModalOpen" max-width="380px" persistent>
      <v-card>
        <v-card-title class="text-lg">
          Change Proficiency Level
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <p class="text-sm text-gray-600 mb-2">
              Change proficiency for <strong>{{ selectedSkillForProficiencyChange?.skillName }}</strong> 
              in <strong>{{ selectedSkillForProficiencyChange?.documentName }}</strong>:
            </p>
            <div class="mb-3">
              <div class="text-xs text-gray-500 mb-1">Current Level:</div>
              <div style="display: flex; align-items: center; gap: 8px;">
                <div class="proficiency-select-segments">
                  <div
                    v-for="(level, levelIndex) in availableProficiencyLevels"
                    :key="level"
                    :class="[
                      'proficiency-select-segment',
                      { 
                        'first': levelIndex === 0,
                        'last': levelIndex === availableProficiencyLevels.length - 1,
                        'filled': selectedSkillForProficiencyChange?.currentRating && levelIndex <= availableProficiencyLevels.indexOf(selectedSkillForProficiencyChange?.currentRating)
                      }
                    ]"
                    :style="selectedSkillForProficiencyChange?.currentRating && levelIndex <= availableProficiencyLevels.indexOf(selectedSkillForProficiencyChange?.currentRating) ? { backgroundColor: getProficiencyColor(selectedSkillForProficiencyChange?.currentRating) } : {}"
                  ></div>
                </div>
                <span class="text-sm text-gray-700">{{ selectedSkillForProficiencyChange?.currentRating || 'Unrated' }}</span>
              </div>
            </div>
          </div>
          
          <v-select
            v-model="newProficiencyLevel"
            :items="getAvailableProficiencyLevels"
            label="New Proficiency Level"
            outlined
            dense
          >
            <template v-slot:item="{ item }">
              <div v-if="item === 'DELETE_SKILL'" class="proficiency-option delete-option w-full">
                <v-icon size="16" color="error" class="mr-2">mdi-delete</v-icon>
                <span class="text-sm font-medium text-red-600">Delete Skill</span>
              </div>
              <div v-else class="proficiency-select-option">
                <!-- Rating segments progression like the main view -->
                <div class="proficiency-select-segments">
                  <div
                    v-for="(level, levelIndex) in availableProficiencyLevels"
                    :key="level"
                    :class="[
                      'proficiency-select-segment',
                      { 
                        'first': levelIndex === 0,
                        'last': levelIndex === availableProficiencyLevels.length - 1,
                        'filled': levelIndex <= availableProficiencyLevels.indexOf(item)
                      }
                    ]"
                    :style="levelIndex <= availableProficiencyLevels.indexOf(item) ? { backgroundColor: getProficiencyColor(item) } : {}"
                  ></div>
                </div>
                <!-- Text -->
                <span class="proficiency-select-text">{{ item }}</span>
              </div>
            </template>
          </v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="proficiencyModalOpen = false">Cancel</v-btn>
          <v-btn 
            color="primary" 
            @click="applyProficiencyChange"
            :disabled="!newProficiencyLevel"
          >
            Apply Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Skill Modal -->
    <v-dialog v-model="addSkillModalOpen" max-width="400px" persistent>
      <v-card>
        <v-card-title class="text-lg">
          Add Missing Skills
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <p class="text-sm text-gray-600 mb-2">
              Add missing focused skills to <strong>{{ selectedDocumentForSkillAddition?.displayName }}</strong>:
            </p>
            <div class="mb-3">
              <div class="text-xs text-gray-500 mb-1">Missing Skills:</div>
              <div class="flex flex-wrap gap-1">
                <span 
                  v-for="skill in documentMissingSkills" 
                  :key="skill"
                  class="px-2 py-1 bg-red-100 text-red-800 rounded text-xs"
                >
                  {{ skill }}
                </span>
              </div>
            </div>
          </div>
          
          <v-select
            v-model="selectedSkillsToAdd"
            :items="documentMissingSkills"
            label="Select Skills to Add"
            outlined
            dense
            multiple
            chips
            small-chips
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="addSkillModalOpen = false">Cancel</v-btn>
          <v-btn 
            color="primary" 
            @click="addSkillsToDocument"
            :disabled="!selectedSkillsToAdd || selectedSkillsToAdd.length === 0"
          >
            Add Skills ({{ selectedSkillsToAdd ? selectedSkillsToAdd.length : 0 }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, nextTick, shallowRef } from 'vue'

export default {
  name: 'SkillBumpChart',
  props: {
    items: {
      type: Array,
      required: true
    },
    focusedSkills: {
      type: Array,
      default: () => []
    },
    onSkillFocus: {
      type: Function,
      default: null
    },
    globalFilters: {
      type: Object,
      default: () => ({
        nameSearch: '',
        selectedTypes: [],
        selectedProficiencyLevels: []
      })
    },
    tags: {
      type: Array,
      default: () => []
    },
    manuallyAddedSkills: {
      type: Array,
      default: () => []
    },
    initialDisplayMode: {
      type: String,
      default: 'bump',
      validator: value => ['bump', 'matrix'].includes(value)
    },
    clientProficiencyLevels: {
      type: Array,
      default: () => ['Unrated']
    },
  },
  emits: ['update:items', 'update-document-data'],
  setup(props, { emit }) {
    const chart = ref(null)
    const showLabels = ref(false)
    const localFocusedSkills = ref(props.focusedSkills ? [...props.focusedSkills] : [])

    // Add proficiency and skill addition modal state
    const proficiencyModalOpen = ref(false)
    const selectedSkillForProficiencyChange = ref(null)
    const newProficiencyLevel = ref('')
    
    const addSkillModalOpen = ref(false)
    const selectedDocumentForSkillAddition = ref(null)
    const selectedSkillsToAdd = ref([])
    const documentMissingSkills = ref([])

    // Add missing timeout variables
    let tooltipTimeout = null
    let resizeTimeout = null

    // Performance optimization: Use shallowRef for non-reactive data
    const skillColorMap = shallowRef(new Map())
    const xPositionCache = shallowRef({})
    const yPositionCache = shallowRef({})
    const pathCache = shallowRef(new Map())

    // Add a reactive trigger to force recalculation - but debounced
    const pathUpdateTrigger = ref(0)
    
    // Add focused skills Set for O(1) lookup performance
    const focusedSkillsSet = ref(new Set())
    

    // Optimized text wrapping with memoization
    const textWrapCache = new Map()
    const wrapText = (text, maxWidth = 12) => {
      if (textWrapCache.has(text)) {
        return textWrapCache.get(text)
      }
      
      if (!text || text.length <= maxWidth) {
        const result = [text]
        textWrapCache.set(text, result)
        return result
      }
      
      const words = text.split(' ')
      const lines = []
      let currentLine = ''
      
      words.forEach(word => {
        if ((currentLine + word).length <= maxWidth) {
          currentLine += (currentLine ? ' ' : '') + word
        } else {
          if (currentLine) {
            lines.push(currentLine)
            currentLine = word
          } else {
            lines.push(word.substring(0, maxWidth))
            currentLine = word.substring(maxWidth)
          }
        }
      })
      
      if (currentLine) {
        lines.push(currentLine)
      }
      
      textWrapCache.set(text, lines)
      return lines
    }

    // Drag state
    const dragState = ref({
      isDragging: false,
      draggedSkill: null,
      draggedDocument: null,
      startY: 0,
      currentY: 0,
      originalSequence: 0,
      targetSequence: 0,
      snapThreshold: 25,
      lastUpdateTime: 0
    })

    // Add flag to prevent clicks after drag
    const justFinishedDrag = ref(false)

    // Watch for changes in focused skills with immediate reactivity
    watch(() => props.focusedSkills, (newVal) => {
      // Force immediate update
      localFocusedSkills.value = [...(newVal || [])]
      
      // Clear path cache when focus changes to force recalculation
      pathCache.value.clear()
      pathUpdateTrigger.value++

    }, { immediate: true, deep: true })

    // Prevent skill click during drag operations - immediate update
    const handleSkillClick = (skillName) => {
      // Do nothing if currently dragging or just finished dragging
      if (dragState.value.isDragging || justFinishedDrag.value) {
        return
      }
      
      const currentSkills = [...localFocusedSkills.value]
      
      if (skillName === null) {
        localFocusedSkills.value = []
      } else {
        const index = currentSkills.indexOf(skillName)
        if (index > -1) {
          currentSkills.splice(index, 1)
        } else {
          currentSkills.push(skillName)
        }
        localFocusedSkills.value = currentSkills
      }
      
      // Synchronize with parent immediately
      if (props.onSkillFocus) {
        nextTick(() => {
          props.onSkillFocus([...localFocusedSkills.value])
        })
      }
    }

    const minDocumentCount = ref(1)
    
    const tooltip = ref({
      show: false,
      x: 0,
      y: 0,
      skill: '',
      document: '',
      sequence: 0,
      value: ''
    })

    // Chart dimensions
    const chartHeight = ref(500)
    const leftMargin = 120
    const rightMargin = 60
    const topMargin = 40
    const bottomMargin = 120
    const minDocumentSpacing = 150

    // Optimized resize handler
    const setChartHeight = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        if (chart.value) {
          chartHeight.value = chart.value.parentElement.offsetHeight || 500
        }
        updatePositionCaches()
      }, 100)
    }

    onMounted(() => {
      setChartHeight()
      window.addEventListener('resize', setChartHeight)
    })
    onUnmounted(() => {
      window.removeEventListener('resize', setChartHeight)
      if (tooltipTimeout) clearTimeout(tooltipTimeout)
      if (resizeTimeout) clearTimeout(resizeTimeout)
    })

    const colors = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#06B6D4', '#F97316', '#84CC16', '#EC4899', '#6366F1']

    // Memoized document processing
    const processedDocuments = computed(() => {
      let allDocuments = []
      
      props.items.forEach(item => {
        if (item.documents && Array.isArray(item.documents)) {
          item.documents.forEach(doc => {
            // Include ALL documents, even those with no tags or empty tags
            allDocuments.push({
              name: doc.jobFamily || doc.name || 'Untitled Document',
              reference: doc.reference || '',
              tags: doc.tags || [], // Always provide an array, even if empty
              parentPath: item.path || [item.name],
              hasValidTags: doc.tags && doc.tags.length > 0
            })
          })
        }
      })
      
      return allDocuments.map(doc => {
        const skills = (doc.tags || []).map(tag => {
          const classifierTypes = tag.classifiers 
            ? tag.classifiers
                .filter(classifier => classifier.value)
                .map(classifier => classifier.value)
            : []

          // FIX: Do not default sequence to 1, use tag.sequence as-is
          return {
            name: tag.value,
            sequence: tag.sequence, // <-- fix here
            value: tag.value,
            rating: tag.name,
            classifierTypes: classifierTypes
          }
        })

        const displayName = doc.reference ? `${doc.name} (${doc.reference})` : doc.name

        return {
          name: doc.name,
          reference: doc.reference,
          displayName: displayName,
          wrappedName: wrapText(displayName),
          skills: skills,
          parentPath: doc.parentPath,
          hasValidTags: doc.hasValidTags
        }
      })
    })

    // Get skill for a specific document in matrix - handle documents with no skills and deleted skills
    const getDocumentSkillForMatrix = (doc, skillName) => {
      if (!doc.skills || doc.skills.length === 0) return null
      // Find the skill, including deleted ones
      const skill = doc.skills.find(skill => skill.name === skillName)
      if (!skill) return null
      // Mark as deleted if sequence is null or isDeleted is true
      if (skill.sequence === null || skill.isDeleted) {
        return { ...skill, isDeleted: true }
      }
      return shouldShowSkill(skill) ? skill : null
    }

    // Enhanced missing skills function to include all focused skills for empty documents
    const getDocumentMissingFocusedSkills = (document) => {
      if (!localFocusedSkills.value || localFocusedSkills.value.length === 0) return []
      
      // If document has no skills at all, all focused skills are missing
      if (!document.skills || document.skills.length === 0) {
        return [...localFocusedSkills.value]
      }
      
      const docSkillNames = new Set(document.skills.map(skill => skill.name))
      return localFocusedSkills.value.filter(skill => !docSkillNames.has(skill))
    }

    // Handle matrix cell click - handle empty documents
    const handleMatrixCellClick = (doc, skillName) => {
      const skill = getDocumentSkillForMatrix(doc, skillName)
      if (skill) {
        const point = {
          skillName: skillName,
          document: doc,
          rating: skill.rating
        }
        showProficiencyModal(point)
      } else {
        // If skill doesn't exist, add it to the document
        addSingleSkillToDocument(doc, skillName)
      }
    }

    // Cached length for template
    const documentsLength = computed(() => processedDocuments.value.length)

    // Optimized chart width calculation
    const chartDisplayWidth = computed(() => {
      const minWidth = 700
      const documentSpacing = Math.max(minDocumentSpacing, 150)
      const calculatedWidth = leftMargin + rightMargin + (documentsLength.value * documentSpacing)
      return Math.max(minWidth, calculatedWidth)
    })

    // --- Merge manuallyAddedSkills into allSkills for bump chart and matrix ---
    const allSkills = computed(() => {
      const skillMap = new Map()
      let colorIndex = 0
      
      processedDocuments.value.forEach(doc => {
        doc.skills.forEach(skill => {
          if (!skillMap.has(skill.name)) {
            const color = colors[colorIndex % colors.length]
            skillMap.set(skill.name, {
              name: skill.name,
              color: color,
              documentCount: 0,
              classifierTypes: skill.classifierTypes || [],
              proficiencyLevels: new Set()
            })
            skillColorMap.value.set(skill.name, color)
            colorIndex++
          }
          const skillEntry = skillMap.get(skill.name)
          skillEntry.documentCount++
          if (skill.rating) {
            skillEntry.proficiencyLevels.add(skill.rating)
          }
        })
      })

      // NEW: add manuallyAddedSkills if not already present
      props.manuallyAddedSkills.forEach(skill => {
        if (!skillMap.has(skill.value)) {
          const color = colors[colorIndex % colors.length]
          skillMap.set(skill.value, {
            name: skill.value,
            color: color,
            documentCount: 0,
            classifierTypes: skill.classifiers
              ? skill.classifiers.filter(c => c.value).map(c => c.value)
              : [],
            proficiencyLevels: new Set()
          })
          skillColorMap.value.set(skill.value, color)
          colorIndex++
        }
      })
      
      // Update skills in documents with consistent colors
      processedDocuments.value.forEach(doc => {
        doc.skills.forEach(skill => {
          skill.color = skillColorMap.value.get(skill.name)
        })
      })
      
      // Convert proficiency levels Set to Array
      return Array.from(skillMap.values()).map(skill => ({
        ...skill,
        proficiencyLevels: Array.from(skill.proficiencyLevels)
      }))
    })

    // Simplified filtered skills - only filter by document count
    const filteredSkills = computed(() => {
      return allSkills.value.filter(skill => {
        const countMatch = skill.documentCount >= minDocumentCount.value
        return countMatch
      })
    })

    const allSequences = computed(() => {
      const sequences = new Set()
      processedDocuments.value.forEach(doc => {
        doc.skills.forEach(skill => {
          sequences.add(skill.sequence)
        })
      })
      return Array.from(sequences).sort((a, b) => a - b)
    })

    // Position calculation functions
    const getDocX = (docIndex) => {
      if (documentsLength.value === 1) return chartDisplayWidth.value / 2
      const sectionWidth = (chartDisplayWidth.value - leftMargin - rightMargin) / documentsLength.value
      return leftMargin + (docIndex * sectionWidth) + (sectionWidth / 2)
    }
    
    const getY = (sequence) => {
      if (!allSequences.value || allSequences.value.length === 0) return chartHeight.value / 2
      if (allSequences.value.length === 1) return chartHeight.value / 2
      
      const minSeq = Math.min(...allSequences.value)
      const maxSeq = Math.max(...allSequences.value)
      const range = maxSeq - minSeq
      
      if (range === 0) return chartHeight.value / 2
      
      const yAxisOffset = 20
      const availableHeight = chartHeight.value - topMargin - bottomMargin - (yAxisOffset * 2)
      const normalizedPosition = (sequence - minSeq) / range
      const yPosition = topMargin + yAxisOffset + (normalizedPosition * availableHeight)
      
      const minYPosition = chartHeight.value - bottomMargin - yAxisOffset
      return Math.min(yPosition, minYPosition)
    }

    // Update position caches
    const updatePositionCaches = () => {
      nextTick(() => {
        // Update X position cache
        const newXCache = {}
        for (let i = 0; i < documentsLength.value; i++) {
          newXCache[i] = getDocX(i)
        }
        xPositionCache.value = newXCache

        // Update Y position cache
        const newYCache = {}
        allSequences.value.forEach(seq => {
          newYCache[seq] = getY(seq)
        })
        yPositionCache.value = newYCache

        // Clear path cache when positions change to force recalculation
        pathCache.value.clear()
      })
    }

    // Watch for changes that require cache updates
    watch([chartDisplayWidth, chartHeight, allSequences], updatePositionCaches, { immediate: true })

    // Get available skill types from all skills
    const availableSkillTypes = computed(() => {
      const typesSet = new Set()
      
      allSkills.value.forEach(skill => {
        if (skill.classifierTypes && skill.classifierTypes.length > 0) {
          skill.classifierTypes.forEach(type => typesSet.add(type))
        }
      })
      
      return Array.from(typesSet).sort()
    })

    // Get available proficiency levels from all skills
    const availableProficiencyLevels = computed(() => {
      const levelsSet = new Set()
      
      allSkills.value.forEach(skill => {
        if (skill.proficiencyLevels && skill.proficiencyLevels.length > 0) {
          skill.proficiencyLevels.forEach(level => levelsSet.add(level))
        }
      })
      
      // Add default proficiency levels that might not be in current data
      console.log(props.clientProficiencyLevels);
      const defaultLevels = [...props.clientProficiencyLevels]//['Basic', 'Intermediate', 'Advanced', 'Expert', 'Strategic']
      defaultLevels.forEach(level => levelsSet.add(level))
      
      return Array.from(levelsSet).sort((a, b) => {
        const order = [...props.clientProficiencyLevels]//['Basic', 'Intermediate', 'Advanced', 'Expert', 'Strategic']
        return order.indexOf(a) - order.indexOf(b)
      })
    })

    // Watch for changes in focused skills to clear path cache and force recalculation
    watch(() => localFocusedSkills.value, () => {
      // Clear path cache when focus changes
      pathCache.value.clear()
      pathUpdateTrigger.value++
    }, { deep: true })

    // New function to get skill path only for visible instances
    const getFilteredSkillPath = (skill) => {
      const points = []
      
      processedDocuments.value.forEach((doc, docIndex) => {
        const docSkill = doc.skills.find(s => s.name === skill.name)
        // Only check shouldShowSkill for name search and document count filtering
        if (docSkill && shouldShowSkill(docSkill)) {
          points.push({
            x: xPositionCache.value[docIndex] || getDocX(docIndex),
            y: yPositionCache.value[docSkill.sequence] || getY(docSkill.sequence)
          })
        }
      })

      if (points.length < 2) {
        return ''
      }

      let path = `M ${points[0].x} ${points[0].y}`
      
      for (let i = 1; i < points.length; i++) {
        const prev = points[i - 1]
        const curr = points[i]
        
        const controlPointDistance = Math.abs(curr.x - prev.x) * 0.4
        const cp1x = prev.x + controlPointDistance
        const cp1y = prev.y
        const cp2x = curr.x - controlPointDistance
        const cp2y = curr.y
        
        path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${curr.x} ${curr.y}`
      }

      return path
    }

    // Enhanced function to get skill segments with individual opacities
    const getFilteredSkillSegments = (skill) => {
      const points = []
      // Only connect points for non-deleted skills (sequence !== null/undefined)
      processedDocuments.value.forEach((doc, docIndex) => {
        const docSkill = doc.skills.find(s => s.name === skill.name)
        if (
          docSkill &&
          shouldShowSkill(docSkill) &&
          docSkill.sequence !== null &&
          docSkill.sequence !== undefined
        ) {
          points.push({
            x: xPositionCache.value[docIndex] || getDocX(docIndex),
            y: yPositionCache.value[docSkill.sequence] || getY(docSkill.sequence),
            docIndex: docIndex,
            skill: docSkill,
            document: processedDocuments.value[docIndex]
          })
        }
      })
      if (points.length < 2) return []
      const segments = []
      for (let i = 0; i < points.length - 1; i++) {
        const startPoint = points[i]
        const endPoint = points[i + 1]
        const startOpacity = getSkillOpacity(skill.name, startPoint.skill.rating)
        const endOpacity = getSkillOpacity(skill.name, endPoint.skill.rating)
        const segmentOpacity = Math.min(startOpacity, endOpacity)
        const controlPointDistance = Math.abs(endPoint.x - startPoint.x) * 0.4
        const cp1x = startPoint.x + controlPointDistance
        const cp1y = startPoint.y
        const cp2x = endPoint.x - controlPointDistance
        const cp2y = endPoint.y
        const segmentPath = `M ${startPoint.x} ${startPoint.y} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${endPoint.x} ${endPoint.y}`
        segments.push({
          pathData: segmentPath,
          opacity: segmentOpacity,
          startDocument: startPoint.document,
          endDocument: endPoint.document,
          startSkill: startPoint.skill,
          endSkill: endPoint.skill
        })
      }
      return segments
    }

    // --- Align deleted skills just above the X axis in the chart ---
    // Patch getY to handle sequence === null (deleted skills)
    const originalGetY = getY
    const getYWithDeleted = (sequence) => {
      if (sequence === null || sequence === undefined) {
        // Align just above the X axis (bottom margin)
        return chartHeight.value - bottomMargin + 18
      }
      return originalGetY(sequence)
    }

    const visibleSkills = computed(() => {
      // Include pathUpdateTrigger and focused skills to force recalculation when they change
      pathUpdateTrigger.value
      const focusedSkillsKey = JSON.stringify([...localFocusedSkills.value].sort())
      
      // Filter skills that have at least one visible instance
      const skillsWithVisibleInstances = new Set()
      
      processedDocuments.value.forEach(doc => {
        doc.skills.forEach(skill => {
          if (shouldShowSkill(skill)) {
            skillsWithVisibleInstances.add(skill.name)
          }
        })
      })
      
      return filteredSkills.value
        .filter(skill => skillsWithVisibleInstances.has(skill.name))
        .map(skill => ({
          name: skill.name,
          color: skill.color,
          segments: getFilteredSkillSegments(skill),
          // Keep the original pathData for backwards compatibility if needed
          pathData: getFilteredSkillPath(skill),
          opacity: getSkillOpacity(skill.name),
          // Add focus key to force reactivity
          _focusKey: focusedSkillsKey
        }))
    })

    // Pre-computed visible data points - show all points that pass name search and document count filtering
    const visibleDataPoints = computed(() => {
      const focusedSkillsKey = JSON.stringify([...localFocusedSkills.value].sort())
      const points = []
      processedDocuments.value.forEach((doc, docIndex) => {
        doc.skills.forEach(skill => {
          if (shouldShowSkill(skill)) {
            const isDeleted = skill.isDeleted || skill.sequence === null || skill.sequence === undefined
            const x = xPositionCache.value[docIndex] || getDocX(docIndex)
            const y = getYWithDeleted(skill.sequence)
            const skillOpacity = getSkillOpacity(skill.name, skill.rating, isDeleted)
            points.push({
              key: `${docIndex}-${skill.name}-${focusedSkillsKey}`,
              x: x,
              y: y,
              color: isDeleted ? '#dc2626' : skill.color,
              skillName: skill.name,
              rating: isDeleted ? null : skill.rating, // Do not show proficiency for deleted
              opacity: skillOpacity,
              showProficiency: localFocusedSkills.value.includes(skill.name),
              isDulled: skillOpacity < 1 && skillOpacity <= 0.3,
              isDeleted: isDeleted,
              skill: skill,
              document: doc,
              _focusKey: focusedSkillsKey
            })
          }
        })
      })
      return points
    })

    // Watch for changes in globalFilters to clear path cache
    watch(() => props.globalFilters, () => {
      // Clear path cache when filters change
      pathCache.value.clear()
      pathUpdateTrigger.value++
    }, { deep: true })

    // Optimized event handlers
    const handleMouseEnter = (event, point) => {
      if (tooltipTimeout) clearTimeout(tooltipTimeout)
      
      tooltip.value = {
        show: true,
        x: event.clientX + 10,
        y: event.clientY - 10,
        skill: point.skillName,
        document: point.document.name,
        sequence: point.skill.sequence,
        value: point.skill.value,
        proficiencyLevel: point.skill.rating || 'No Level'
      }
    }

    const handleMouseLeave = () => {
      tooltipTimeout = setTimeout(() => {
        tooltip.value.show = false
      }, 100)
    }

    const shouldShowSkill = (skill) => {
      const skillData = allSkills.value.find(s => s.name === skill.name)
      if (!skillData) return false
      
      // Document count filter - ALWAYS hide if below threshold
      const countMatch = skillData.documentCount >= minDocumentCount.value
      if (!countMatch) return false
      
      // Name search filter - ALWAYS hide if doesn't match
      if (props.globalFilters?.nameSearch) {
        const searchTerm = props.globalFilters.nameSearch.toLowerCase()
        if (!skill.name.toLowerCase().includes(searchTerm)) {
          return false
        }
      }
      
      // Type filter - ALWAYS hide if doesn't match (since legend controls this)
      if (props.globalFilters?.selectedTypes && props.globalFilters.selectedTypes.length > 0 && props.globalFilters.selectedTypes.length < availableSkillTypes.value.length) {
        const skillTypes = skillData.classifierTypes || []
        const hasMatchingType = skillTypes.some(type => 
          props.globalFilters.selectedTypes.includes(type)
        )
        if (skillTypes.length > 0 && !hasMatchingType) {
          return false
        }
      }
      
      // Proficiency filters should NOT hide skills - they should only dull them
      return true
    }

    // Updated skill opacity calculation - only dull based on proficiency, not types
    const getSkillOpacity = (skillName, skillRating = null, isDeleted = false) => {
      const skillData = allSkills.value.find(s => s.name === skillName)
      if (!skillData) return 0.2
      
      // If skill is deleted, show very low opacity
      if (isDeleted) return 0.1
      
      // Check if skill should be dulled due to proficiency filters ONLY
      let isDulled = false
      
      // Proficiency level filter dulling - check at instance level
      if (props.globalFilters?.selectedProficiencyLevels && props.globalFilters.selectedProficiencyLevels.length > 0 && props.globalFilters.selectedProficiencyLevels.length < availableProficiencyLevels.value.length) {
        // If a specific skill rating is provided, check that instance
        if (skillRating !== null) {
          if (skillRating && !props.globalFilters.selectedProficiencyLevels.includes(skillRating)) {
            isDulled = true
          }
          // If skill has no rating but filter is active and not all levels selected, dull it
          if (!skillRating) {
            isDulled = true
          }
        } else {
          // Fallback to skill-level check
          const skillLevels = skillData.proficiencyLevels || []
          const hasMatchingLevel = skillLevels.some(level => 
            props.globalFilters.selectedProficiencyLevels.includes(level)
          )
          if (skillLevels.length > 0 && !hasMatchingLevel) {
            isDulled = true
          }
        }
      }
      
      // If dulled by proficiency filters, return reduced opacity
      if (isDulled) return 0.3
      
      // Otherwise use focus-based opacity
      if (!localFocusedSkills.value || localFocusedSkills.value.length === 0) {
        return 1
      } else if (localFocusedSkills.value.includes(skillName)) {
        return 1
      } else {
        return 0.2
      }
    }

    const missingFocusedSkills = computed(() => {
      if (!localFocusedSkills.value || localFocusedSkills.value.length === 0) return []
      
      // Get all skill names that pass the show filters (name search, type, and document count)
      const visibleSkillNames = allSkills.value
        .filter(skill => {
          // Apply show/hide filtering logic (name search, type, and document count)
          if (props.globalFilters?.nameSearch) {
            const searchTerm = props.globalFilters.nameSearch.toLowerCase()
            if (!skill.name.toLowerCase().includes(searchTerm)) {
              return false
            }
          }
          
          // Type filter - hide if doesn't match
          if (props.globalFilters?.selectedTypes && props.globalFilters.selectedTypes.length > 0 && props.globalFilters.selectedTypes.length < availableSkillTypes.value.length) {
            const skillTypes = skill.classifierTypes || []
            const hasMatchingType = skillTypes.some(type => 
              props.globalFilters.selectedTypes.includes(type)
            )
            if (skillTypes.length > 0 && !hasMatchingType) {
              return false
            }
          }
          
          // Document count is already handled in allSkills computation
          return true
        })
        .map(s => s.name)
      
      return localFocusedSkills.value.filter(skill => !visibleSkillNames.includes(skill))
    })

    const handleMouseDown = (event, point) => {
      event.preventDefault()
      event.stopPropagation()
      
      // Only allow dragging if skill is focused
      if (!localFocusedSkills.value.includes(point.skillName)) {
        // If skill is not focused, focus it first but don't start dragging
        localFocusedSkills.value.push(point.skillName)
        if (props.onSkillFocus) {
          props.onSkillFocus([...localFocusedSkills.value])
        }
        return
      }
      
      dragState.value = {
        isDragging: true,
        draggedSkill: point.skill,
        draggedDocument: point.document,
        startY: event.clientY,
        currentY: event.clientY,
        originalSequence: point.skill.sequence,
        targetSequence: point.skill.sequence,
        snapThreshold: 25,
        lastUpdateTime: Date.now()
      }

      const handleDragMove = (event) => {
        if (!dragState.value.isDragging) return
        event.preventDefault()
        
        dragState.value.currentY = event.clientY
        
        if (chart.value) {
          const rect = chart.value.getBoundingClientRect()
          const relativeY = event.clientY - rect.top
          dragState.value.targetSequence = getSequenceFromY(relativeY)
        }
      }

      const handleDragEnd = (event) => {
        if (!dragState.value.isDragging) return
        event.preventDefault()

        const newSequence = dragState.value.targetSequence
        
        if (newSequence !== dragState.value.originalSequence) {
          updateSkillPriority(dragState.value.draggedDocument, dragState.value.draggedSkill, newSequence)
          
          // Force immediate cache clearing and recalculation
          nextTick(() => {
            pathCache.value.clear()
            updatePositionCaches()
            pathUpdateTrigger.value++
          })
        }

        // Clean up event listeners
        document.removeEventListener('mousemove', handleDragMove)
        document.removeEventListener('mouseup', handleDragEnd)
        document.body.style.cursor = 'default'
        document.body.style.userSelect = ''
        
        // Set flag to prevent immediate click events
        justFinishedDrag.value = true
        
        // Reset drag state
        dragState.value = {
          isDragging: false,
          draggedSkill: null,
          draggedDocument: null,
          startY: 0,
          currentY: 0,
          originalSequence: 0,
          targetSequence: 0,
          snapThreshold: 25,
          lastUpdateTime: 0
        }
        
        // Clear the flag after a short delay
        setTimeout(() => {
          justFinishedDrag.value = false
        }, 150)
      }

      document.addEventListener('mousemove', handleDragMove, { passive: false })
      document.addEventListener('mouseup', handleDragEnd, { passive: false })
      document.body.style.cursor = 'grabbing'
      document.body.style.userSelect = 'none'
    }

    const getSequenceFromY = (y) => {
      if (!allSequences.value || allSequences.value.length === 0) return 1

      const minSeq = Math.min(...allSequences.value)
      const maxSeq = Math.max(...allSequences.value)
      
      const yAxisOffset = 20
      const chartTop = topMargin + yAxisOffset
      const chartBottom = chartHeight.value - bottomMargin - yAxisOffset
      const snapThreshold = 30
      
      const clampedY = Math.max(chartTop, Math.min(chartBottom, y))
      
      let closestSequence = minSeq
      let minDistance = Infinity
      
      // Find the closest sequence position
      for (const seq of allSequences.value) {
        const seqY = yPositionCache.value[seq] || getY(seq)
        const distance = Math.abs(clampedY - seqY)
        if (distance < minDistance) {
          minDistance = distance
          closestSequence = seq
        }
      }
      
      return closestSequence
    }

    const updateSkillPriority = (document, skill, newSequence) => {
      
      // Find the document in items array (original data) FIRST - before any modifications
      let targetItem = null
      let targetDoc = null
      
      for (const item of props.items) {
        if (item.documents) {
          const doc = item.documents.find(d => 
            (d.jobFamily || d.name) === document.name && 
            (d.reference || '') === (document.reference || '')
          )
          if (doc) {
            targetItem = item
            targetDoc = doc
            break
          }
        }
      }
      
      if (!targetDoc || !targetDoc.tags) {
        return
      }
      
      // Find the skill tag to update
      const skillTag = targetDoc.tags.find(tag => tag.value === skill.name)
      if (!skillTag) {
        return
      }
      
      const currentSequence = skillTag.sequence || 1
      
      // If no change, return early
      if (newSequence === currentSequence) {
        return
      }
      
      
      // EMIT DOCUMENT UPDATE EVENT FIRST - before making changes
      // This ensures the main component can capture the original state
      emit('update-document-data', {
        key: `${document.name}|${document.reference || 'no-ref'}`,
        data: {
          documentName: document.name,
          documentReference: document.reference || '',
          doc_id: targetDoc.doc_id,
          dp_id: targetDoc.dp_id,
          dpa_id: targetDoc.dpa_id,
          tpa_id: targetDoc.tpa_id,
          tp_id: targetDoc.tp_id,
          dp_name: targetDoc.dp_name,
          tag_type_id: targetDoc.tag_type_id,
          source: 'bump-chart-priority-change',
          changeType: 'priority',
          skillName: skill.name,
          oldSequence: currentSequence,
          newSequence: newSequence
        }
      })
      
      // Now make the actual changes to the data
      // Get all tags sorted by current sequence
      const allTags = [...targetDoc.tags].sort((a, b) => (a.sequence || 1) - (b.sequence || 1))
      
      // Remove the dragged skill from its current position
      const draggedTagIndex = allTags.findIndex(tag => tag.value === skill.name)
      if (draggedTagIndex === -1) return
      const draggedTag = allTags.splice(draggedTagIndex, 1)[0]
      
      // Calculate the correct insertion index
      // newSequence is 1-based, but we need 0-based index for insertion
      // Also need to account for the fact that we already removed one item
      let insertIndex = newSequence - 1
      // Clamp the insert index to valid bounds
      insertIndex = Math.max(0, Math.min(insertIndex, allTags.length))
      
      // Insert at the new position
      allTags.splice(insertIndex, 0, draggedTag)
      
      // Update all sequences to be consecutive starting from 1
      // Preserve all other tag properties including tag_type_id
      allTags.forEach((tag, index) => {
        tag.sequence = index + 1
        // Ensure tag_type_id is preserved - fallback to document's tag_type_id if missing
        if (!tag.tag_type_id && targetDoc.tag_type_id) {
          tag.tag_type_id = targetDoc.tag_type_id
        }
      })
      
      // Update the original document tags
      targetDoc.tags = allTags
      
      // Create a deep copy of the items array for the update:items event
      const updatedItems = JSON.parse(JSON.stringify(props.items))
      
      // Emit the updated items to maintain reactivity
      emit('update:items', updatedItems)
      
      // Force UI update
      nextTick(() => {
        pathCache.value.clear()
        updatePositionCaches()
        pathUpdateTrigger.value++
      })
    }

    // Add the missing currentTargetSequence computed property
    const currentTargetSequence = computed(() => {
      return dragState.value.targetSequence
    })

    // Grid lines with cached positions
    const yGridLines = computed(() => allSequences.value.map(seq => yPositionCache.value[seq] || getY(seq)))
    const xGridLines = computed(() => {
      const lines = []
      for (let i = 0; i < documentsLength.value; i++) {
        lines.push(xPositionCache.value[i] || getDocX(i))
      }
      return lines
    })

    // Watch for changes in processedDocuments to clear path cache
    watch(() => processedDocuments.value, () => {
      // Clear path cache when document data changes
      pathCache.value.clear()
      pathUpdateTrigger.value++
    }, { deep: true })

    // Enhanced skill point click handler for proficiency editing
    const handleSkillPointClick = (point) => {
      // If skill is focused, allow proficiency editing, otherwise focus the skill
      if (localFocusedSkills.value.includes(point.skillName)) {
        showProficiencyModal(point)
      } else {
        handleSkillClick(point.skillName)
      }
    }

    // Show proficiency modal
    const showProficiencyModal = (point) => {
      selectedSkillForProficiencyChange.value = {
        skillName: point.skillName,
        documentName: point.document.displayName,
        currentRating: point.rating,
        point: point,
        isDeleted: point.isDeleted
      }
      newProficiencyLevel.value = ''
      proficiencyModalOpen.value = true
    }

    // Apply proficiency change
    const applyProficiencyChange = () => {
      if (!selectedSkillForProficiencyChange.value || !newProficiencyLevel.value) return

      const point = selectedSkillForProficiencyChange.value.point
      
      // Emit document update event FIRST - before making changes
      emit('update-document-data', {
        key: `${point.document.name}|${point.document.reference || 'no-ref'}`,
        data: {
          documentName: point.document.name,
          documentReference: point.document.reference || '',
          source: 'bump-chart-proficiency-change',
          changeType: newProficiencyLevel.value === 'DELETE_SKILL' ? 'delete' : 'proficiency',
          skillName: point.skillName,
          newProficiencyLevel: newProficiencyLevel.value
        }
      })

      // Find the actual document and skill in the original data
      let targetDoc = null
      for (const item of props.items) {
        if (item.documents) {
          const doc = item.documents.find(d => 
            (d.jobFamily || d.name) === point.document.name && 
            (d.reference || '') === (point.document.reference || '')
          )
          if (doc) {
            targetDoc = doc
            break
          }
        }
      }

      if (targetDoc) {
        const skillTag = targetDoc.tags.find(tag => tag.value === point.skillName)
        if (skillTag) {
          if (newProficiencyLevel.value === 'DELETE_SKILL') {
            // Record previous proficiency before deletion
            if (!skillTag.isDeleted) {
              skillTag._previousName = skillTag.name
              if (skillTag._originalSequence === undefined) {
                skillTag._originalSequence = skillTag.sequence
              }
            }
            skillTag.isDeleted = true
            skillTag.sequence = null
            // Reorder remaining skills
            const activeTags = targetDoc.tags.filter(t => !t.isDeleted)
            activeTags
              .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
              .forEach((tag, index) => {
                tag.sequence = index + 1
              })
            targetDoc.tags.filter(t => t.isDeleted).forEach(tag => { tag.sequence = null })
          } else {
            // If the skill was previously deleted and we're setting a new proficiency, restore it
            if (skillTag.isDeleted) {
              skillTag.isDeleted = false
              // Restore previous proficiency if available
              if (skillTag._previousName && skillTag._previousName !== 'DELETE_SKILL' && skillTag._previousName !== null) {
                skillTag.name = skillTag._previousName
                skillTag.tag_type_rating_id = getRatingIdForProficiency(skillTag._previousName)
              } else if (newProficiencyLevel.value === 'Unrated') {
                skillTag.name = null
                skillTag.tag_type_rating_id = null
              } else {
                skillTag.name = newProficiencyLevel.value
                skillTag.tag_type_rating_id = getRatingIdForProficiency(newProficiencyLevel.value)
              }
              delete skillTag._previousName
              // Reorder skills after restoration
              const allTags = targetDoc.tags
              const activeTags = allTags.filter(tag => !tag.isDeleted && tag !== skillTag)
              const deletedTags = allTags.filter(tag => tag.isDeleted)
              const originalSequence = skillTag._originalSequence !== undefined
                ? skillTag._originalSequence
                : skillTag.sequence
              if (skillTag._originalSequence === undefined) {
                skillTag._originalSequence = originalSequence
              }
              let insertPosition = activeTags.length
              for (let i = 0; i < activeTags.length; i++) {
                if (activeTags[i].sequence >= originalSequence) {
                  insertPosition = i
                  break
                }
              }
              insertPosition = Math.max(0, originalSequence - 1)
              activeTags.splice(insertPosition, 0, skillTag)
              activeTags.forEach((tag, index) => {
                tag.sequence = index + 1
              })
              deletedTags.forEach(tag => { tag.sequence = null })
            } else {
              if (newProficiencyLevel.value === 'Unrated') {
                skillTag.name = null
                skillTag.tag_type_rating_id = null
              } else {
                skillTag.name = newProficiencyLevel.value
                skillTag.tag_type_rating_id = getRatingIdForProficiency(newProficiencyLevel.value)
              }
            }
          }
        }
      }

      proficiencyModalOpen.value = false
      
      nextTick(() => {
        pathCache.value.clear()
        pathUpdateTrigger.value++
      })
    }

    // Get available proficiency levels with delete option for modal
    const getAvailableProficiencyLevels = computed(() => {
      if (selectedSkillForProficiencyChange.value?.isDeleted) {
        // For deleted skills, don't show DELETE_SKILL option
        return availableProficiencyLevels.value
      }
      return [...availableProficiencyLevels.value, 'DELETE_SKILL']
    })

    // Add display mode control
    const displayMode = ref(props.initialDisplayMode)

    // Add matrix-specific computed properties
    const canShowMatrix = computed(() => {
      return processedDocuments.value.length > 0 && allSkills.value.length > 0
    })

    const proficiencyLevels = computed(() => {
      const levels = [...availableProficiencyLevels.value]//['Unrated', 'Basic', 'Intermediate', 'Advanced', 'Expert', 'Strategic']
      return levels
    })

    const matrixSkills = computed(() => {
      // --- Ensure all manually added skills are included in the matrix, even if not present in any document ---
      const baseSkills = allSkills.value.filter(skill => {
        // Always include manually added skills
        if (props.manuallyAddedSkills && props.manuallyAddedSkills.some(ms => ms.value === skill.name)) {
          return true
        }
        // Otherwise, include if present in any document
        return processedDocuments.value.some(doc => 
          doc.skills.some(s => s.name === skill.name && shouldShowSkill(s))
        )
      })

      // Prioritize focused skills at the top, then matching, then non-matching, then alphabetical
      const focusedSet = new Set(localFocusedSkills.value)
      return baseSkills
        .map(skill => {
          const isFocused = focusedSet.has(skill.name)
          return {
            ...skill,
            isFocused,
            sortPriority: isFocused ? 0 : 1
          }
        })
        .sort((a, b) => {
          if (a.sortPriority !== b.sortPriority) {
            return a.sortPriority - b.sortPriority
          }
          // Alphabetical sort by name (case-insensitive)
          return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        })
    })

    // Get proficiency color for matrix
    const getProficiencyColor = (level) => {
      const colors = {
        'Unrated': '#e5e7eb',
        'Basic': '#AAAAAA',
        'Foundational': '#AAAAAA',
        'Intermediate': '#CEDC26',
        'Developing': '#CEDC26',
        'Advanced': '#FFCB00',
        'Proficient': '#50B3CB',
        'Expert': '#50B3CB',
        'Strategic': '#ED742D'
      }
      return colors[level] || colors['Unrated']
    }

    // Get proficiency abbreviation for matrix
    const getProficiencyAbbreviation = (level) => {
      return level.substring(0,1).toUpperCase();
      /*const abbreviations = {
        'Unrated': 'U',
        'Basic': 'B',
        'Foundational': 'F',
        'Intermediate': 'I', 
        'Developing': 'D',
        'Advanced': 'A',
        'Expert': 'E',
        'Strategic': 'S'
      }
      return abbreviations[level] || 'U'*/
    }

    // Get rating color classes (matching DocumentDetails)
    const getRatingColor = (ratingName) => {
      const colors = {
        'Basic': 'bg-gray-100 text-gray-800 border-gray-300',
        'Foundational': 'bg-gray-100 text-gray-800 border-gray-300',
        'Intermediate': 'bg-lime-100 text-lime-800 border-lime-300',
        'Developing': 'bg-lime-100 text-lime-800 border-lime-300',
        'Proficient': 'bg-cyan-100 text-cyan-800 border-cyan-300',
        'Advanced': 'bg-yellow-100 text-yellow-800 border-yellow-300',
        'Expert': 'bg-cyan-100 text-cyan-800 border-cyan-300',
        'Strategic': 'bg-orange-100 text-orange-800 border-orange-300'
      }
      return colors[ratingName] || 'bg-gray-100 text-gray-800 border-gray-300'
    }

    // Get rating ID for proficiency (matching DocumentDetails)
    const getRatingIdForProficiency = (proficiencyName) => {
      if (!proficiencyName || proficiencyName === 'Unrated') {
        return null
      }

      // Find the rating ID from the tags prop data
      for (const tagType of props.tags) {
        if (tagType.ratings && tagType.ratings.length > 0) {
          const rating = tagType.ratings.find(r => r.name === proficiencyName)
          if (rating) {
            return rating.tag_type_rating_id
          }
        }
      }

      // Fallback to basic mapping if not found in tags data
      const ratingMap = {
        'Basic': 1,
        'Intermediate': 2, 
        'Advanced': 3,
        'Expert': 4,
        'Strategic': 5
      }

      return ratingMap[proficiencyName] || null
    }

    // Show add skill modal
    const showAddSkillModal = (document, docIndex) => {
      selectedDocumentForSkillAddition.value = document
      documentMissingSkills.value = getDocumentMissingFocusedSkills(document)
      selectedSkillsToAdd.value = []
      addSkillModalOpen.value = true
    }

    // Add skills to document
    const addSkillsToDocument = () => {
      if (!selectedDocumentForSkillAddition.value || !selectedSkillsToAdd.value || selectedSkillsToAdd.value.length === 0) {
        return
      }

      const doc = selectedDocumentForSkillAddition.value

      emit('update-document-data', {
        key: `${doc.name}|${doc.reference || 'no-ref'}`,
        data: {
          documentName: doc.name,
          documentReference: doc.reference || '',
          source: 'bump-chart-add-skills',
          changeType: 'add-multiple',
          skillNames: selectedSkillsToAdd.value
        }
      })

      let targetDoc = null
      for (const item of props.items) {
        if (item.documents) {
          const foundDoc = item.documents.find(d =>
            (d.jobFamily || d.name) === doc.name &&
            (d.reference || '') === (doc.reference || '')
          )
          if (foundDoc) {
            targetDoc = foundDoc
            break
          }
        }
      }

      if (targetDoc) {
        const currentTags = targetDoc.tags || []
        const maxSequence = currentTags.length > 0
          ? Math.max(...currentTags.map(tag => tag.sequence || 0))
          : 0

        selectedSkillsToAdd.value.forEach((skillName, index) => {
          // PATCH: Search both tags and manuallyAddedSkills for skill definition
          let skillTagDef = props.tags.find(tag => tag.value === skillName)
          if (!skillTagDef && props.manuallyAddedSkills) {
            skillTagDef = props.manuallyAddedSkills.find(tag => tag.value === skillName)
          }
          if (skillTagDef) {
            //const tempTagValueId = `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            const newTag = {
              tag_value_id: null,
              value: skillName,
              name: null,
              tag_type_rating_id: null,
              sequence: maxSequence + index + 1,
              classifiers: skillTagDef.classifiers || [],
              tag_type_id: targetDoc.tag_type_id || skillTagDef.tag_type_id,
              description: skillTagDef.description || '',
              isNewSkill: true,
              doc_id: targetDoc.doc_id
            }
            if (!targetDoc.tags) {
              targetDoc.tags = []
            }
            targetDoc.tags.push(newTag)
          }
        })
      }

      addSkillModalOpen.value = false

      nextTick(() => {
        pathCache.value.clear()
        pathUpdateTrigger.value++
      })
    }

    const addSingleSkillToDocument = (doc, skillName) => {
      emit('update-document-data', {
        key: `${doc.name}|${doc.reference || 'no-ref'}`,
        data: {
          documentName: doc.name,
          documentReference: doc.reference || '',
          source: 'bump-chart-add-single-skill',
          changeType: 'add-single',
          skillName: skillName
        }
      })

      let targetDoc = null
      for (const item of props.items) {
        if (item.documents) {
          const foundDoc = item.documents.find(d =>
            (d.jobFamily || d.name) === doc.name &&
            (d.reference || '') === (doc.reference || '')
          )
          if (foundDoc) {
            targetDoc = foundDoc
            break
          }
        }
      }

      if (targetDoc) {
        // PATCH: Search both tags and manuallyAddedSkills for skill definition
        let skillTagDef = props.tags.find(tag => tag.value === skillName)
        if (!skillTagDef && props.manuallyAddedSkills) {
          skillTagDef = props.manuallyAddedSkills.find(tag => tag.value === skillName)
        }
        if (skillTagDef) {
          const currentTags = targetDoc.tags || []
          const maxSequence = currentTags.length > 0
            ? Math.max(...currentTags.map(tag => tag.sequence || 0))
            : 0

          //const tempTagValueId = `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

          const newTag = {
            tag_value_id: null,
            value: skillName,
            name: null,
            tag_type_rating_id: null,
            sequence: maxSequence + 1,
            classifiers: skillTagDef.classifiers || [],
            tag_type_id: targetDoc.tag_type_id || skillTagDef.tag_type_id,
            description: skillTagDef.description || '',
            isNewSkill: true,
            doc_id: targetDoc.doc_id
          }

          if (!targetDoc.tags) {
            targetDoc.tags = []
          }
          targetDoc.tags.push(newTag)

          nextTick(() => {
            pathCache.value.clear()
            pathUpdateTrigger.value++
          })
        }
      }
    }

    // Add helpers for focus/dull logic in matrix
    const isSkillFocused = (skillName) => {
      return localFocusedSkills.value && localFocusedSkills.value.length > 0 && localFocusedSkills.value.includes(skillName)
    }
    const shouldDullSkill = (skillName) => {
      return localFocusedSkills.value && localFocusedSkills.value.length > 0 && !localFocusedSkills.value.includes(skillName)
    }

    // Undo delete from matrix (and bump chart)
    const undoDeleteSkillFromMatrix = (doc, skillName) => {
      let targetDoc = null
      for (const item of props.items) {
        if (item.documents) {
          const foundDoc = item.documents.find(d =>
            (d.jobFamily || d.name) === doc.name &&
            (d.reference || '') === (doc.reference || '')
          )
          if (foundDoc) {
            targetDoc = foundDoc
            break
          }
        }
      }
      if (!targetDoc) return
      const tag = (targetDoc.tags || []).find(t => t.value === skillName)
      if (!tag) return

      emit('update-document-data', {
        key: `${doc.name}|${doc.reference || 'no-ref'}`,
        data: {
          documentName: doc.name,
          documentReference: doc.reference || '',
          source: 'bump-chart-undo-delete',
          changeType: 'undo-delete',
          skillName: skillName
        }
      })

      tag.isDeleted = false
      // Restore previous proficiency value if available
      if (tag._previousName && tag._previousName !== 'DELETE_SKILL' && tag._previousName !== null) {
        tag.name = tag._previousName
        tag.tag_type_rating_id = getRatingIdForProficiency(tag._previousName)
      } else {
        tag.name = null
        tag.tag_type_rating_id = null
      }
      delete tag._previousName

      reorderSkillsAfterRestoration(targetDoc, tag)
      nextTick(() => {
        pathCache.value.clear()
        pathUpdateTrigger.value++
      })
    }

    // Helper for restoring skill position (same logic as DocumentDetails)
    function reorderSkillsAfterRestoration(doc, restoredTag) {
      const allTags = doc.tags
      const activeTags = allTags.filter(tag => !tag.isDeleted && tag !== restoredTag)
      const deletedTags = allTags.filter(tag => tag.isDeleted)
      // Use _originalSequence if available, else fallback to sequence
      const originalSequence = restoredTag._originalSequence !== undefined
        ? restoredTag._originalSequence
        : restoredTag.sequence
      if (restoredTag._originalSequence === undefined) {
        restoredTag._originalSequence = originalSequence
      }
      let insertPosition = activeTags.length
      for (let i = 0; i < activeTags.length; i++) {
        if (activeTags[i].sequence >= originalSequence) {
          insertPosition = i
          break
        }
 }
      // Always insert at the original sequence index (1-based), so insert at originalSequence-1 (0-based)
      insertPosition = Math.max(0, originalSequence - 1)
      activeTags.splice(insertPosition, 0, restoredTag)
      activeTags.forEach((tag, index) => {
        tag.sequence = index + 1
      })
      deletedTags.forEach(tag => {
        tag.sequence = null
      })
    }

    return {
      chart,
      showLabels,
      localFocusedSkills,
      minDocumentCount,
      tooltip,
      dragState,
      currentTargetSequence,
      pathUpdateTrigger,
      justFinishedDrag,
      processedDocuments,
      documentsLength,
           allSkills,
      filteredSkills,
      allSequences,
      yGridLines,
      xGridLines,
      chartHeight,
      chartDisplayWidth,
      leftMargin,
      rightMargin,
      topMargin,
      bottomMargin,
      xPositionCache,
      yPositionCache,
      visibleSkills,
      visibleDataPoints,
      availableSkillTypes,
      availableProficiencyLevels,
      handleMouseEnter,
      handleMouseLeave,
      handleSkillClick,
      getSkillOpacity,
      shouldShowSkill,
      wrapText,
      missingFocusedSkills,
      handleMouseDown,
      getSequenceFromY,
      updateSkillPriority,
      proficiencyModalOpen,
      selectedSkillForProficiencyChange,
      newProficiencyLevel,
      addSkillModalOpen,
      selectedDocumentForSkillAddition,
      selectedSkillsToAdd,
      documentMissingSkills,
      handleSkillPointClick,
      getDocumentMissingFocusedSkills,
      showProficiencyModal,
      applyProficiencyChange,
      showAddSkillModal,
      addSkillsToDocument,
      getRatingColor,
      displayMode,
      canShowMatrix,
      proficiencyLevels,
      matrixSkills,
      getProficiencyColor,
      getProficiencyAbbreviation,
      getDocumentSkillForMatrix,
      handleMatrixCellClick,
      addSingleSkillToDocument,
      getAvailableProficiencyLevels,
      undoDeleteSkillFromMatrix,
      isSkillFocused,
      shouldDullSkill
    }
  }
}
</script>

<style scoped>
.bump-chart-container {
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 14px;
  color: #333;
  height: calc(100vh - 365px);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.chart-controls-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 16px 5px 16px;
  margin-bottom: 0;
  background: #f9f9f9;
  border-bottom: 1px solid #ddd;
}

.controls-group {
  display: flex;
  gap: 24px;
  align-items: center;
}

.revert-controls {
  display: flex;
  align-items: center;
}

.revert-btn {
  font-size: 12px !important;
  height: 32px !important;
}

.chart-header {
  background: #f9f9f9;
  padding: 16px;
  border-bottom: 1px solid #ddd;
}

.chart-title {
  margin: 0;
  font-size: 18px;
  font-weight: bold;
}

.chart-info {
  margin-top: 4px;
  font-size: 12px;
  color: #666;
}

.chart-content {
  padding: 0px;
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: row;
  overflow-y: hidden;
}

.legend-sidebar {
  float: left;
  width: 500px;
  margin-right: 20px;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.legend-header {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;
}

.controls-section {
  margin-bottom: 16px;
}

.control-group {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.control-label {
  font-size: 14px;
  color: #333;
}

.control-select-multi {
  flex: 1;
  margin-right: 8px;
}

.control-number {
  width: 100px;
}

.legend-controls {
  text-align: right;
}

.legend-button {
  font-size: 14px;
  padding: 6px 12px;
}

.legend-list {
  overflow-y: auto;
}

.legend-item {
  display: flex;
  align-items: center;
  padding: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.legend-item:hover {
  background: #f1f1f1;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: 8px;
}

.legend-text {
  flex: 1;
  font-size: 14px;
  color: #333;
}

.skill-count {
  font-size: 12px;
  color: #666;
  margin-left: 4px;
}

.legend-indicator {
  font-size: 18px;
  color: #3B82F6;
  margin-left: 8px;
}

.chart-wrapper {
  display: block;
  overflow-x: auto;
  margin-top: 5px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
  height: 100%;
  min-height: 0;
}

.chart-scroll-container {
  width: 100%;
  min-width: 0;
  overflow: auto;
  display: block;
  height: 100%;
  min-height: 0;
  overflow-y:hidden
}

.chart {
  width: auto;
  min-width: 100%;
  height: 100%;
  min-height: 0;
  display: block;
}

.grid-lines line {
  stroke: #e0e0e0;
}

.axes line {
  stroke: #333;
  stroke-width: 2;
}

.y-axis-labels text {
  font-size: 12px;
  fill: #666;
}

.x-axis-labels text {
  font-size: 11px;
  fill: #666;
}

.connecting-lines path {
  stroke-width: 3;
  fill: none;
}

.data-points circle {
  cursor: pointer;
  transition: transform 0.15s ease, opacity 0.15s ease;
  transform-origin: center;
  transform-box: fill-box;
}

.data-points circle:hover {
  transform: scale(1.2);
  opacity: 1;
}

.data-points circle.dragging {
  transition: none;
  z-index: 1000;
  transform: scale(1.3);
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
}

.data-points circle.snapping {
  transform: scale(1.4);
  stroke: #3b82f6;
  stroke-width:  4;
}

.y-axis-label {
  transition: fill 0.1s ease, font-weight 0.1s ease;
}

.priority-highlight {
  animation: pulse 0.8s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.6; }
}

.snap-line {
  animation: dash 0.8s linear infinite;
}

@keyframes dash {
  to { stroke-dashoffset: -10; }
}

.priority-change-text {
  text-shadow: 
    -1px -1px 0 white,
    1px -1px 0 white,
    -1px 1px 0 white,
    1px 1px 0 white;
  paint-order: stroke fill;
  animation: fadeIn 0.2s ease-in;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.no-data {
  text-align: center;
  padding: 50px 0;
  color: #999;
}

.tooltip {
  position: fixed;
  background: white;
  border: 1px solid #ddd;
  padding: 8px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  font-size: 12px;
  z-index: 1000;
  pointer-events: none;
}

.missing-skill-indicator {
  opacity: 1;
  box-shadow: 0 0 0 2px #fca5a5;
  font-size: 13px;
  display: inline-flex;
}

.add-skill-icon {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.proficiency-label {
  transition: opacity 0.2s ease;
}

.proficiency-label:hover {
  opacity: 0.7;
}

.proficiency-option {
  padding: 4px 0;
}

/* Color classes for proficiency levels */
.bg-gray-100 { background-color: #f3f4f6; }
.text-gray-800 { color: #1f2937; }
.border-gray-300 { border-color: #d1d5db; }
.bg-blue-100 { background-color: #dbeafe; }
.text-blue-800 { color: #1e40af; }
.border-blue-300 { border-color: #93c5fd; }
.bg-green-100 { background-color: #dcfce7; }
.text-green-800 { color: #166534; }
.border-green-300 { border-color: #86efac; }
.bg-purple-100 { background-color: #f3e8ff; }
.text-purple-800 { color: #6b21a8; }
.border-purple-300 { border-color: #c4b5fd; }
.bg-orange-100 { background-color: #fed7aa; }
.text-orange-800 { color: #9a3412; }
.border-orange-300 { border-color: #fdba74; }
.bg-red-100 { background-color: #fee2e2; }
.text-red-800 { color: #991b1b; }

.heat-matrix-container {
  padding: 0;
  background: white;
  border-radius: 8px;
  min-height: 500px;
  max-width: 100%;
  overflow: visible;
}

.matrix-wrapper {
  overflow: auto;
  max-height: calc(100vh - 405px);
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 20px;
}

.matrix-table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  min-width: fit-content;
}

.matrix-corner-header {
  background: #f9fafb;
  padding: 16px;
  min-width: 240px;
  width: 240px;
  position: sticky;
  left: 0;
  top: 0;
  z-index: 3;
  border-right: 2px solid #e5e7eb;
  border-bottom: 2px solid #e5e7eb;
}

.matrix-doc-header {
  background: #f9fafb;
  padding: 12px 8px;
  text-align: center;
  vertical-align: middle;
  min-height: 60px;
  min-width: 120px;
  width: 120px;
  position: sticky;
  top: 0;
  z-index: 2;
  border-bottom: 2px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
}

.matrix-skill-header {
  background: #f3f4f6;
  padding: 12px 16px;
  text-align: left;
  vertical-align: middle;
  font-weight: 600;
  color: #374151;
  min-width: 240px;
  width: 240px;
  position: sticky;
  left: 0;
  z-index: 1;
  border-right: 2px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* Ensure full row height alignment */
  height: 57px;
  min-height: 57px;
  box-sizing: border-box;
}

/* Ensure the <th> fills the row height */
.matrix-table th.matrix-skill-header {
  height: 57px;
  min-height: 57px;
  box-sizing: border-box;
  vertical-align: middle;
  padding-top: 0;
  padding-bottom: 0;
}

.matrix-cell {
  background: white;
  padding: 4px;
  text-align: center;
  vertical-align: middle;
  min-height: 48px;
  min-width: 120px;
  width: 120px;
  border-bottom: 1px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
}

.matrix-cell-clickable {
  cursor: pointer;
}

.matrix-cell:hover {
  background: #f9fafb;
}



.proficiency-square {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  transition: all 0.2s ease;
  cursor: pointer;
  margin: 0 auto;
}

.proficiency-square:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.empty-cell {
  background: #f3f4f6;
  border: 2px dashed #d1d5db;
  color: #9ca3af;
  overflow: hidden;
}

.empty-cell:hover {
  border-color: #10b981;
}

.add-skill-indicator {
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.add-skill-indicator:hover {
  background: #10b981 !important;
  color: white;
  border-color: #10b981;
}

.skill-name {
  flex: 1;
  text-align: left;
}

.skill-color-indicator {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-left: 8px;
}

.doc-header-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.doc-header-line {
  font-size: 11px;
  font-weight: bold;
  color: #374151;
  line-height: 1.3;
  margin-bottom: 2px;
}

.doc-header-line:last-child {
  margin-bottom: 0;
}

.matrix-legend {
  background: #f9fafb;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.legend-title {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
}

.legend-items {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-square {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 12px;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.legend-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.proficiency-legend-inline {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 24px;
}

.legend-label-inline {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.legend-items-inline {
  display: flex;
  align-items: center;
  gap: 12px;
}

.legend-item-inline {
  display: flex;
  align-items: center;
  gap: 4px;
}

.legend-square-inline {
  width: 18px;
  height: 18px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 10px;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.legend-text-inline {
  font-size: 12px;
  font-weight: 500;
  color: #374151;
}

/* Deleted skill point styles */
.skill-deleted-point {
  stroke-dasharray: 3,3;
  opacity: 0.4 !important;
}

.skill-deleted-point:hover {
  opacity: 0.6 !important;
}

/* Delete option in proficiency modal */
.delete-option {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  color: #dc2626 !important;
  background-color: #fef2f2;
  border-top: 1px solid #fca5a5;
  margin-top: 4px;
}

.delete-option:hover {
  background-color: #fee2e2 !important;
}

/* Proficiency Select Option Styling */
.proficiency-select-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  width: 100%;
}

.proficiency-select-segments {
  display: flex;
  gap: 1px;
  align-items: center;
}

.proficiency-select-segment {
  width: 12px;
  height: 12px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
}

.proficiency-select-segment.first {
  border-radius: 6px 2px 2px 6px;
}

.proficiency-select-segment.last {
  border-radius: 2px 6px 6px 2px;
}

.proficiency-select-segment.filled {
  border: 1px solid transparent;
}

.proficiency-select-text {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.proficiency-square.deleted-matrix-skill {
  background: #fee2e2 !important;
  color: #dc2626 !important;
  border: 1px solid #fca5a5 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
  text-decoration: line-through;
  opacity: 0.7;
  pointer-events: none;
}

/* Matrix focus/dull styles */
.matrix-cell-focused {
  opacity: 1 !important;
  filter: none !important;
  background: #e0f2fe !important;
  /* Optionally: border: 2px solid #3b82f6; */
}
.matrix-cell-dulled {
  opacity: 0.3 !important;
  filter: grayscale(40%);
  background: #f9fafb !important;
}
.matrix-skill-focused .matrix-skill-header {
  background: #e0f2fe !important;
  color: #2563eb !important;
}
.matrix-skill-dulled .matrix-skill-header {
  opacity: 0.3 !important;
  filter: grayscale(40%);
  background: #f9fafb !important;
  color: #888 !important;
}

/* Compact Min Documents Filter */
.min-docs-filter {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-right: 16px;
}

.filter-label {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;
}

.compact-number-input {
  width: 50px;
  height: 24px;
  padding: 2px 6px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  text-align: center;
  background: white;
}

.compact-number-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 1px #3b82f6;
}
</style>